import { useEffect, useSyncExternalStore } from 'react'

export interface PositionSpec {
  name: string
  emoji: string
}

/** `specs`: seçeneklerde hepsi, bir üyenin seçiminde yalnızca seçtikleri. */
export interface MemberPosition {
  id: number
  name: string
  emoji: string
  specs: PositionSpec[]
}

/** Simge ipucu ve dışa aktarım: `Air Force (Havoc, Gunner)`. */
export function positionLabel(p: MemberPosition): string {
  return p.specs.length ? `${p.name} (${p.specs.map(s => s.name).join(', ')})` : p.name
}

/**
 * Kendi pozisyonların, tek kopya: kenar çubuğundaki kart ve Profil sayfası aynı
 * şeyi gösterir, biri değişince öteki yeniden yüklemeden görür.
 */
interface MyPositionsState {
  positions: MemberPosition[]
  options: MemberPosition[]
  loaded: boolean
  saveFailed: boolean
}

let state: MyPositionsState = { positions: [], options: [], loaded: false, saveFailed: false }
let inflight: Promise<void> | null = null
const listeners = new Set<() => void>()

function update(patch: Partial<MyPositionsState>) {
  state = { ...state, ...patch }
  listeners.forEach(l => l())
}

function fetchState(): Promise<void> {
  inflight ??= fetch('/api/members/me/positions', { credentials: 'include' })
    .then(r => (r.ok ? r.json() : null))
    .then(d => { if (d) update({ positions: d.positions, options: d.options, loaded: true }) })
    .catch(() => { /* çevrimdışı: son bilinen hâl durur */ })
    .finally(() => { inflight = null })
  return inflight
}

export function useMyPositions(): MyPositionsState {
  useEffect(() => { if (!state.loaded) void fetchState() }, [])
  return useSyncExternalStore(
    cb => { listeners.add(cb); return () => { listeners.delete(cb) } },
    () => state,
  )
}

// Art arda dokunuşlar sırayla gönderilir, yoksa geç gelen eski istek yeni seçimi
// ezebilirdi.
let queue: Promise<unknown> = Promise.resolve()

export function saveMyPositions(next: MemberPosition[]) {
  update({ positions: next, saveFailed: false })
  queue = queue.then(async () => {
    try {
      const res = await fetch('/api/members/me/positions', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          positions: next.map(p => ({ id: p.id, specs: p.specs.map(s => s.name) })),
        }),
      })
      if (!res.ok) throw new Error(String(res.status))
    } catch {
      // Ekranda kayıtsız bir seçim kalmasın: sunucudaki hâle dön.
      update({ saveFailed: true })
      await fetchState()
    }
  })
}
